import React from "react";
import * as Icon from "react-bootstrap-icons";
import ReactTooltip from "react-tooltip";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

type RepairItemData = {
	repairId: number;
	cat_Id: number;
	updatedOn: string;
	updatedBy: string;
	isResolved: boolean;
	isUpdateable: boolean;
	category: string;
	subCategory: string;
	remedy: string;
	repairTypes?: string[];
	repairType?: string;
	repairedOn?: string;
	description?: string;
};

type RepairData = {
	repairType: string;
	repairDate: Date | null;
	description: string;
	repairId?: number;
};

interface RepairItemProps {
	item: RepairItemData;
	backgroundColor: string;
	priorityLevel: string; // 'high', 'medium', or 'low'
	testId: string;
	repairData?: RepairData;
	editingRepairId: number | null;
	onStatusChange: (isResolved: boolean, cat_Id: number, repairId: number, repairTypes?: string[]) => void;
	onRepairTypeChange: (cat_Id: number, repairType: string) => void;
	onRepairDateChange: (cat_Id: number, date: Date | null) => void;
	onRepairDescriptionChange: (cat_Id: number, description: string) => void;
	onSaveRepair: (cat_Id: number) => void;
	onCancelEdit: (cat_Id: number) => void;
	onStartEdit: (cat_Id: number, repairId: number, selectedRepairType: string, repairedOn: string, description: string, repairTypes: string[]) => void;
	formatDate: (dateString: string) => string;
}

const RepairItem: React.FC<RepairItemProps> = ({
	item,
	backgroundColor,
	priorityLevel,
	testId,
	repairData,
	editingRepairId,
	onStatusChange,
	onRepairTypeChange,
	onRepairDateChange,
	onRepairDescriptionChange,
	onSaveRepair,
	onCancelEdit,
	onStartEdit,
	formatDate,
}) => {
	const isEditing = editingRepairId === item.repairId;
	const showRepairForm = (!item.isResolved && repairData) || (item.isResolved && isEditing);
	const currentRepairData = repairData || {
		repairType: item.repairType  || "",
		repairDate: item.repairedOn ? new Date(item.repairedOn) : null,
		description: item.description || "",
	};

	return (
		<div className="form-controls mb-2" id="form-controls" style={{ backgroundColor }}>
			<div className="label">{item.category}</div>
			<div className="label">{item.subCategory}</div>
			<div className="form-value">{item.remedy}</div>
			{item.isUpdateable ? (
				<div className="col">
					<div className="d-flex justify-content-between align-items-center">
						<div className="form-check form-switch form-check-inline p-0">
							<label className="form-check-label">Repaired</label>
							<input
								className="form-check-input float-end pointer ms-1"
								data-testid={testId}
								checked={item.isResolved || !!repairData}
								type="checkbox"
								role="switch"
								onChange={() => onStatusChange(item.isResolved, item.cat_Id, item.repairId, item.repairTypes)}
							/>
						</div>
						{/* {item.isResolved && (
							<span className="label">
								{formatDate(item.repairedOn || "")}
								<Icon.InfoCircleFill
									className="info-icon m-1 ms-2"
									role="button"
									data-tip={item.repairedOn}
									data-for={item.repairedOn}
									id="info-icon"
									style={{ width: '0.9rem', height: '1rem', color: '#817d7d' }}
								/>
								<ReactTooltip id={item?.repairedOn} place="bottom" type="success" effect="solid">
									Updated By: {item.repairedOn || ""}
								</ReactTooltip>
							</span>
						)} */}
					</div>
					{/* Show repair form when toggle is on but not yet saved, or when editing */}
					{showRepairForm && (
						<div className="mt-2 p-2 border rounded bg-light repair-form-container">
							<div className="mb-2">
								<label className="form-label small" htmlFor={`repair-type-${priorityLevel}-${item.cat_Id}`}>
									Repair Type:
								</label>
								<select
									id={`repair-type-${priorityLevel}-${item.cat_Id}`}
									name={`repair-type-${priorityLevel}-${item.cat_Id}`}
									className="form-select form-select-sm"
									value={currentRepairData.repairType || ""}
									onChange={(e) => onRepairTypeChange(item.cat_Id, e.target.value)}
									style={{ zIndex: 1001, position: 'relative' }}
								>
									{Array.isArray(item.repairTypes) && item.repairTypes.length > 0 ? (
										item.repairTypes.map((type, idx) => (
											<option key={idx} value={type}>{type}</option>
										))
									) : (
										<option value="">No repair types available</option>
									)}
								</select>
							</div>
							<div className="mb-2">
								<label className="form-label small" htmlFor={`repair-date-${priorityLevel}-${item.cat_Id}`}>
									Repair Date:
								</label>
								<DatePicker
									id={`repair-date-${priorityLevel}-${item.cat_Id}`}
									name={`repair-date-${priorityLevel}-${item.cat_Id}`}
									selected={currentRepairData.repairDate}
									onChange={(date) => onRepairDateChange(item.cat_Id, date)}
									// showTimeInput
									// timeInputLabel="Time:"
									dateFormat="MM/dd/yyyy"
									className="form-control form-control-sm"
									placeholderText="MM/DD/YYYY"
									popperClassName="repair-datepicker-popper"
									maxDate={new Date()}
									filterDate={(date) => {
										const today = new Date();
										today.setHours(0, 0, 0, 0);
										const selectedDate = new Date(date);
										selectedDate.setHours(0, 0, 0, 0);
										return selectedDate <= today;
									}}
								/>
							</div>
							<div className="mb-2">
								<label className="form-label small" htmlFor={`repair-description-${priorityLevel}-${item.cat_Id}`}>
									Description :
								</label>
								<textarea
									id={`repair-description-${priorityLevel}-${item.cat_Id}`}
									name={`repair-description-${priorityLevel}-${item.cat_Id}`}
									className="form-control form-control-sm"
									rows={3}
									value={currentRepairData.description || ""}
									onChange={(e) => onRepairDescriptionChange(item.cat_Id, e.target.value)}
									placeholder="Enter repair description..."
								/>
							</div>
							<button
								className="btn btn-sm btn-primary me-2"
								onClick={() => onSaveRepair(item.cat_Id)}
							>
								Save
							</button>
							<button
								className="btn btn-sm btn-secondary"
								onClick={() => onCancelEdit(item.cat_Id)}
							>
								Cancel
							</button>
						</div>
					)}
					{/* Show resolved repair info when not editing */}
					{item.isResolved && !isEditing && (
						<div className="mt-2 me-2 p-1">
							<div className="d-flex justify-content-between align-items-start">
								<div>
									<div className="label small">Repair Info</div>
									{item.repairType && (
										<div className="label small">Repair Type: {item.repairType}</div>
									)}
									{item.description && (
										<div className="label small">Description: {item.description}</div>
									)}
									{item.repairedOn && (
										<div className="label small">Repair Date: {formatDate(item.repairedOn)}</div>
									)}
									{item.updatedBy && (
										<div className="label small">Repair By: {item.updatedBy}</div>
									)}
								</div>
								<div >
									<Icon.PencilSquare
										className="icon pointer ms-2"
										onClick={() => onStartEdit(
											item.cat_Id,
											item.repairId,
											item.repairType || "",
											item.repairedOn || "",
											item.description || "",
											item.repairTypes || []
										)}
										style={{ width: '1rem', height: '1rem', color: '#817d7d' }}
									/>
								</div>
							</div>
							
						</div>
					)}
				</div>
			) : (
				<div className="pb-1" />
			)}
		</div>
	);
};

export default RepairItem;

